import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { combineLatest, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Store, createSelector, select } from '@ngrx/store';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { TranslationService } from "@core/translation.service";
import { UserErrorsService } from '@app/user/user-core/services/user-errors.service';
import { UserState } from '@app/user/user.reducer';
import { ValidatorsUtil } from '@helper/validators-util';
import { notNull } from '@helper/operators';
import { UserProfile } from '@helper/abstraction/user';
import { environment } from 'src/environments/environment';

export type ProxyPopupForm = {
	login: string;
	unp: string;
	checkUnp: boolean;
	password: string;
	reasonForCancellation: string;
	firstName?: string;
    lastName?: string;
    middleName?: string;
    jobPost?: string;
};

export type ProxyAction = "ANNULMENT" | "CONFIRM_ANNULMENT" | "DENY_ANNULMENT" | "CLARIFICATION" | "CORRECT" | "REPLACE" | "CREATE_CORRECTION";

@Component({
	selector: 'app-proxy-popup',
	templateUrl: './proxy-popup.component.html',
	styleUrls: ['./proxy-popup.component.scss']
})
export class ProxyPopupComponent implements OnInit {
	public certificateType?: string | null;
	public form?: FormGroup;
	public name?: string;
	public action?: ProxyAction;
	public defaultHeader = "Для доступа к контейнеру личного ключа введите пароль";
	public mainStoreName = "user";
	public env = environment;
	private userProfile?: UserProfile;
	private userProfile$: Observable<UserProfile | undefined>;
	private unsubscribe$$: Subject<void> = new Subject<void>();

	constructor(
		public config: DynamicDialogConfig,
		public ref: DynamicDialogRef,
		private userErrorsService: UserErrorsService,
		private store: Store,
		private fb: FormBuilder,
		private translationService: TranslationService,
	) {
		this.certificateType = sessionStorage.getItem("CertificateType");
		this.config.header = this.config?.data?.header ?? this.defaultHeader;
		this.action = this.config?.data?.action;
		this.name = this.config?.data?.name;
		this.mainStoreName = this.config?.data?.mainStoreName ?? "user";
		this.config.closable = false;
		this.config.width = "600px";
		this.initForm();
		const selectUser = (appState: any): any => appState[this.mainStoreName];
		const checkUnp = createSelector(selectUser, (state: any): boolean | undefined => state?.userProfile?.checkUnp);
		const selectCertificateName = createSelector(selectUser, (state: any): string | undefined => state?.userProfile?.certificateName);
		const selectUnp = createSelector(selectUser, (state: any): string | undefined => state?.organizationInfo?.unp);
		const userProfile = createSelector(selectUser, (state: UserState): UserProfile | undefined => state?.userProfile);
		this.userProfile$ = this.store.pipe(select(userProfile));
		this.userProfile$.pipe(notNull(), takeUntil(this.unsubscribe$$)).subscribe(user => {
			this.userProfile = user;
		});
		combineLatest(
			this.store.pipe(select(selectCertificateName)),
			this.store.pipe(select(checkUnp)),
			this.store.pipe(select(selectUnp))
		).pipe(
			notNull(),
			take(1)
		).subscribe(([certificateName, checkUnp, unp]) => {
			this.form?.patchValue({ login: certificateName });
			if (checkUnp) {
				this.form?.patchValue({ checkUnp, unp });
			}
		});
	}

	// @HostListener('window:keydown.enter', ['$event'])
	// public handleKeyDown(): void {
	// 	this.confirm(this.form!);
	// }

	public ngOnInit(): void {
		if (this.name) {
			this.form?.patchValue({ login: this.name });
		}
		if (this.action && !["DENY_ANNULMENT", "CLARIFICATION"].includes(this.action)) {
			["firstName", "lastName", "middleName", "jobPost"].forEach(field => {
				if ((this.userProfile as any)?.[field])
					this.form?.patchValue({ 
						[field]: (this.userProfile as any)?.[field],
					});
			});
		}
	}

	public confirm(form: FormGroup): void {
		ValidatorsUtil.triggerValidation(form);
		if (form.invalid) {
			this.userErrorsService.displayErrors(form, undefined, undefined, true);
			return;
		}
		let fV = form.getRawValue();
		for (let k in fV) {
			if (["", null, undefined].includes(fV[k])) {
				delete fV[k];
			}
		}
		this.ref.close(fV);
	}

	public checkErrorName(): string {
		return this.translationService.getTranslation(this.action!);
	}

	private initForm(): void {
		this.form = this.fb.group({
			reasonForCancellation: [this.config.data?.form?.reasonForCancellation ?? null],
			unp: [this.config.data?.form?.unp ?? null],
			checkUnp: [this.config.data?.form?.checkUnp ?? null],
			login: [this.config.data?.form?.login ?? null],
			password: [this.config.data?.form?.password ?? null],
			firstName: [this.config.data?.form?.firstName ?? null],
			lastName: [this.config.data?.form?.lastName ?? null],
			middleName: [this.config.data?.form?.middleName ?? null],
			jobPost: [this.config.data?.form?.jobPost ?? null],
		});

		if (!["KZ"].includes(this.env.project) && (this.certificateType === "certificate" || !this.action)) {
			["login", "password"].forEach(field => {
				this.form?.get(field)?.setValidators([Validators.required]);
				this.form?.get(field)?.updateValueAndValidity({ emitEvent: false });
			});
		}

		if (this.action) {
			if (!["DENY_ANNULMENT", "CLARIFICATION"].includes(this.action)) {
				["reasonForCancellation", "firstName", "lastName", "jobPost"].forEach(field => {
					this.form?.get(field)?.setValidators([Validators.required]);
					this.form?.get(field)?.updateValueAndValidity({ emitEvent: false });
				});
			}
			if (["DENY_ANNULMENT", "CLARIFICATION"].includes(this.action)) {
				["reasonForCancellation"].forEach(field => {
					this.form?.get(field)?.setValidators([Validators.required]);
					this.form?.get(field)?.updateValueAndValidity({ emitEvent: false });
				});
			}
		}
	}

	public close(): void {
		this.ref.close();
	}
}
